import CrudRequest from '@rootCommon/services/crud-request';
import { BaseMessageDto, BrandingLogoDto, IdType } from '@rootCommon/types/common';

enum VerificationType {
  INVITATION = 'invitation',
  VERIFICATION = 'verification',
}

class EmailVerificationService {
  private makePath = (
    prefix: string,
    routeType: string,
    suffix: string,
    routeExtension?: string,
  ) => `${prefix}/${routeType}-${suffix}/${routeExtension || ''}`;

  private resend = async (
    id: IdType,
    prefix: string,
    suffix: VerificationType,
    routeExtension?: string,
  ): Promise<BaseMessageDto> => {
    const res = await CrudRequest.get<BaseMessageDto>(
      this.makePath(prefix, 'resend', suffix, routeExtension),
      { params: { id } },
    );
    return res.data;
  };

  resendInvitation = (
    id: IdType,
    prefix: string,
    routeExtension?: string,
  ) => this.resend(id, prefix, VerificationType.INVITATION, routeExtension);

  resendVerification = (
    id: IdType,
    prefix: string,
    routeExtension?: string,
  ) => this.resend(id, prefix, VerificationType.VERIFICATION, routeExtension);

  passwordReset = async (
    id: number,
    prefix: string,
    routeExtension?: string,
  ): Promise<BaseMessageDto> => {
    const res = await CrudRequest.put<BaseMessageDto, null>(
      this.makePath(prefix, 'reset', 'password', routeExtension),
      null,
      { params: { id } },
    );
    return res.data;
  };

  getLogo = async (
    email: string,
    prefix: string,
  ): Promise<number | null> => {
    let logoId;
    try {
      const logoResponse = await CrudRequest.get<BrandingLogoDto>(
        `${prefix}/dashboard-branding-logo`,
        { params: { email } },
      );
      logoId = logoResponse.data.dashboardBrandingLogoFileId;
    } catch (e) {
      logoId = null;
    }
    return logoId;
  };

  verifyByCodeAndGetLogo = async (
    email: string,
    code: string,
    prefix: string,
  ): Promise<{ logoId: number | null; message: string }> => {
    const codeResponse = await CrudRequest.get<BaseMessageDto>(
      `${prefix}/verify-code`,
      { params: { email, code } },
    );

    let logoId;
    try {
      const logoResponse = await CrudRequest.get<BrandingLogoDto>(
        `${prefix}/dashboard-branding-logo`,
        { params: { email } },
      );
      logoId = logoResponse.data.dashboardBrandingLogoFileId;
    } catch (e) {
      logoId = null;
    }
    return { logoId, message: codeResponse.data.message };
  };

  invitationSubmitPassword = async (
    email: string,
    newPassword: string,
    prefix: string,
    isForgotPassword: boolean,
  ): Promise<BaseMessageDto> => {
    const response = await CrudRequest.put<BaseMessageDto, {
      email: string;
      newPassword: string;
      isForgotPassword: boolean;
    }>(
      `${prefix}/set-password`,
      { email, newPassword, isForgotPassword },
    );
    return response.data;
  };
}

export default new EmailVerificationService();
